// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { fontConfig } from "@stoneage/XDL/constants";
import { css } from "styled-components";
import { color, ellipsis, xScreen } from "@stoneage/XDL/Utilities";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { ProductAttributesStyles } from "@stoneage/Overrides/Components/ProductAttributes";

interface ProductGridStyles {
    container?: InjectableCss;
    imageWrapper?: InjectableCss;
    productImageStyles?: ProductImageStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    partNumbersContainer?: InjectableCss;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    attributesContainer?: InjectableCss;
    attributesStyles?: ProductAttributesStyles;
}

const descriptionStyles: TypographyPresentationProps = {
    ...fontConfig.regularSingleLine,
    weight: fontConfig.weight.bold,
    css: css`
        margin-bottom: 0;
        align-self: baseline;
        ${ellipsis(2)}
        ${wrapInContainerStyles}
    `,
};

export const productGridStyles: ProductGridStyles = {
    container: {
        css: css`
            position: relative;
            display: grid;
            grid-template-rows: subgrid;
            grid-row: span ${({ gridRowSpan }: any) => gridRowSpan};
            gap: 8px;
            width: 100%;

            ${xScreen("lte", "sm")} {
                grid-template-columns: 100px 1fr;
            }
        `,
    },
    imageWrapper: {
        css: css`
            ${xScreen("lte", "sm")} {
                grid-column-start: 1;
                grid-column-end: 2;
                grid-row-start: 1;
                grid-row-end: 4;
            }
        `,
    },
    productImageStyles: {
        linkWrappingImage: {
            css: css`
                max-width: 308px;
                max-height: 308px;
            `,
        },
        img: {
            width: "308px",
            height: "308px",
        },
    },
    productDescriptionStyles: {
        productDetailLink: {
            color: "secondary",
            typographyProps: {
                ...descriptionStyles,
            },
            css: css`
                align-self: baseline;
            `,
        },
        productDetailDescription: {
            ...descriptionStyles,
        },
    },
    attributesContainer: {
        css: css`
            border-bottom: 1px solid ${color("common.border")};
            padding-bottom: 8px;
            ${xScreen("lte", "sm")} {
                grid-column-start: 2;
                grid-column-end: 3;
            }
        `,
    },
    attributesStyles: {
        container: {
            css: css`
                display: flex;
                flex-direction: column;
                gap: 4px;
            `,
        },
        itemContainer: {
            css: css`
                display: flex;
                flex-direction: row;
                gap: 4px;
            `,
        },
        attributeTypeText: {
            ...fontConfig.xSmallSingleLine,
            weight: fontConfig.weight.regular,
        },
        attributeValueText: {
            ...fontConfig.xSmallSingleLine,
            weight: fontConfig.weight.medium,
        },
    },
    partNumbersContainer: {
        css: css`
            &.divider {
                border-bottom: 1px solid ${color("common.border")};
                padding-bottom: 8px;
            }
            ${xScreen("lte", "sm")} {
                grid-column-start: 2;
                grid-column-end: 3;
            }
        `,
    },
};
